import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { bindSubmit, escape, field, setStatus, statusZone } from '../components/form.js';
import { toast } from '../components/toast.js';
import { resumePendingInvite } from '../lib/pending_invite.js';
import {
  exchangeSocialToken,
  loadSocialAuthConfig,
  prefetchSocialScripts,
  renderGoogleButton,
  signInWithFacebook,
} from '../lib/social_auth.js';
import { navigate } from '../router.js';

const STATUS_ID = 'signup-status';
// Survives the page redirect from /r/CODE → /signup and is also re-read
// from sessionStorage on direct /signup loads so reloading the page
// during signup doesn't lose the attribution.
const REFERRAL_STORAGE_KEY = 'triviana.referral_code';

function _readReferralCode() {
  // 1. Explicit `?ref=` on the current URL wins — that's the freshest
  //    intent (someone just clicked a brand-new invite link).
  const qs = new URLSearchParams(window.location.search);
  const fromQuery = qs.get('ref');
  if (fromQuery) {
    sessionStorage.setItem(REFERRAL_STORAGE_KEY, fromQuery);
    return fromQuery;
  }
  // 2. Otherwise pick up whatever was stashed by an earlier visit /
  //    /r/CODE redirect.
  return sessionStorage.getItem(REFERRAL_STORAGE_KEY) || '';
}

function _clearStoredReferralCode() {
  try {
    sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch { /* private browsing edge — best effort */ }
}

export function renderSignup(root) {
  // Kick off the GSI script download in parallel with the rest of this
  // page render so the social buttons paint as soon as the section
  // below the form appears. See social_auth.js for the rationale.
  prefetchSocialScripts();

  const referralCode = _readReferralCode();

  root.innerHTML = `
    <div class="appbar">
      <a href="/onboarding" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Create account</div>
    </div>

    <div class="pad">
      <div id="signup-invite-banner"></div>
      <p class="lede-muted">Email + password. We'll send a 6-digit code to confirm it's you.</p>

      <form id="signup-form" class="form card" novalidate>
        ${field({
          name: 'email',
          label: 'Email',
          type: 'email',
          autocomplete: 'email',
          placeholder: 'you@example.com',
        })}
        ${field({
          name: 'password',
          label: 'Password',
          type: 'password',
          autocomplete: 'new-password',
          placeholder: 'min 8 chars, letter + digit',
        })}
        <label class="checkbox-row">
          <input type="checkbox" name="is_18_plus" />
          <span>I confirm I am 18 or older.</span>
        </label>
        ${statusZone(STATUS_ID)}
        <button type="submit" class="btn">Create account</button>
      </form>

      <div id="social-buttons"></div>

      <div class="center" style="margin-top:14px;font-size:12.5px;">
        Already registered? <a href="/login" data-link>Log in</a>
      </div>
      <div class="fine-print">18+ · Play responsibly · Licensed operator</div>
    </div>
  `;

  if (referralCode) {
    // Fire-and-forget — the banner just confirms the inviter; a 404
    // here (typo'd code) is a quiet failure, not an error.
    void _renderReferralBanner(referralCode);
  }

  mountSocialButtonsForSignup(document.getElementById('social-buttons'), { referralCode });

  const form = document.getElementById('signup-form');
  bindSubmit(form, async (data) => {
    setStatus(STATUS_ID, { message: 'Creating your account…', kind: 'info' });

    if (!data.is_18_plus) {
      setStatus(STATUS_ID, { message: 'You must confirm you are 18 or older.', kind: 'error' });
      return;
    }

    try {
      const user = await api('/auth/register', {
        method: 'POST',
        body: {
          email: data.email,
          password: data.password,
          is_18_plus: true,
          // Bad / self / unknown codes are silently dropped server-side;
          // a registration NEVER fails because of a bad ref code. We
          // clear the stash either way so a refresh doesn't re-apply.
          ...(referralCode ? { referral_code: referralCode } : {}),
        },
      });
      _clearStoredReferralCode();

      // Auto-login. Where we navigate next depends on whether the backend
      // requires email verification — when REQUIRE_EMAIL_VERIFICATION=false
      // the register response comes back already-verified and we go straight
      // to the dashboard.
      const tokens = await api('/auth/login', {
        method: 'POST',
        body: { email: data.email, password: data.password },
      });
      auth.setSession(tokens);
      auth.setMe(user);

      // If they arrived here from a 1-on-1 invite link, resume that flow
      // straight into the match room instead of the onboarding detour.
      if (await resumePendingInvite()) return;

      if (user.email_verified) {
        // Closed-alpha / no-verification mode: jump straight into the
        // pick-your-topics step. They can skip it from there if they want.
        toast.success('Welcome to Triviana. Pick a few topics to get started.');
        navigate('/interests');
      } else {
        toast.success('Account created. Check your email for a 6-digit code.');
        navigate('/verify-email');
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Something went wrong.';
      setStatus(STATUS_ID, { message: msg, kind: 'error' });
    }
  });
}

async function mountSocialButtonsForSignup(container, { referralCode = '' } = {}) {
  if (!container) return;
  const config = await loadSocialAuthConfig();
  const hasGoogle = !!config.google_client_id;
  const hasFacebook = !!config.facebook_app_id;
  if (!hasGoogle && !hasFacebook) {
    container.style.display = 'none';
    return;
  }
  // Social buttons now sit BELOW the email form — the divider leads the
  // user into the alternative, replacing the previous "or use email"
  // wording that pointed back at the form.
  container.innerHTML = `
    <div class="row center" style="margin:14px 0 10px;font-size:11px;color:var(--mute);">
      <span style="flex:1;border-top:1px solid var(--line);"></span>
      <span style="padding:0 9px;">or sign up with</span>
      <span style="flex:1;border-top:1px solid var(--line);"></span>
    </div>
    <div class="card" style="padding:14px;">
      <div id="google-btn-host" style="display:flex;justify-content:center;margin-bottom:${hasFacebook ? '8' : '0'}px;"></div>
      ${hasFacebook
        ? '<button type="button" id="facebook-btn" class="btn" style="background:#1877F2;color:#fff;width:100%;">Sign up with Facebook</button>'
        : ''}
    </div>
  `;
  if (hasGoogle) {
    renderGoogleButton(document.getElementById('google-btn-host'), {
      clientId: config.google_client_id,
      label: 'signup_with',
      onCredential: (idToken) => completeSocialSignup({
        provider: 'google', token: idToken, referralCode,
      }),
    }).catch((err) => console.error('google sign-up init', err));
  }
  if (hasFacebook) {
    document.getElementById('facebook-btn').addEventListener('click', async () => {
      try {
        const token = await signInWithFacebook({ appId: config.facebook_app_id });
        if (!token) return;
        await completeSocialSignup({ provider: 'facebook', token, referralCode });
      } catch (err) {
        toast.error(err?.message || 'Facebook sign-up failed.');
      }
    });
  }
}

async function completeSocialSignup({ provider, token, referralCode = '' }) {
  setStatus(STATUS_ID, { message: 'Creating your account…', kind: 'info' });
  try {
    const tokens = await exchangeSocialToken({
      provider,
      token,
      referralCode: referralCode || null,
    });
    _clearStoredReferralCode();
    auth.setSession(tokens);
    const me = await api('/me');
    auth.setMe(me);
    if (await resumePendingInvite()) return;
    toast.success(`Welcome to Triviana${me.username ? ', ' + me.username : ''}.`);
    // Brand-new social signups go to interests; returning users (already
    // have a username = they've finished onboarding) go straight home.
    navigate(me.username ? '/' : '/interests');
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Social sign-up failed.';
    setStatus(STATUS_ID, { message: msg, kind: 'error' });
  }
}

async function _renderReferralBanner(code) {
  const host = document.getElementById('signup-invite-banner');
  if (!host) return;
  try {
    const lookup = await api(`/referrals/code/${encodeURIComponent(code)}`);
    if (!lookup || !lookup.inviter_name) {
      // Server returned 200 with inviter_name=null → unknown code.
      // Drop the stashed code so a refresh doesn't re-try, leave the
      // banner empty (signup still works, just without attribution).
      _clearStoredReferralCode();
      return;
    }
    const name = lookup.inviter_name;
    host.innerHTML = `
      <div class="card" style="padding:12px;margin-bottom:14px;background:rgba(233,75,162,0.10);border:1px solid rgba(233,75,162,0.45);">
        <div class="row" style="align-items:center;gap:10px;">
          <div style="font-size:24px;line-height:1;">🎯</div>
          <div class="grow">
            <div style="font-family:var(--font-display);font-size:13px;color:#fff;font-weight:700;">
              Invited by ${escape(name)}
            </div>
            <div class="muted" style="font-size:11.5px;line-height:1.45;margin-top:2px;">
              Sign up + play your first live trivia, and your inviter earns a 50/50 power-up. You get to keep yours.
            </div>
          </div>
        </div>
      </div>
    `;
  } catch (err) {
    // 404 / network error — quietly leave the banner empty. The code is
    // still attached to the form submit; the banner is just nice-to-have.
    if (err instanceof ApiError && err.status === 404) {
      _clearStoredReferralCode();
    }
  }
}
